import type { Dispatch, SetStateAction } from 'react';
import type { IGSetupGuideItem } from '../types';

import { BlockStack, Button, Collapsible, Icon, InlineStack, Text } from '@shopify/polaris';
import { GSetupGuideItemDescription } from './GSetupGuideItemDescription';
import { useGSetupGuideContext } from '../contexts/GSetupGuideProvider';
import { CheckCircleIcon, CircleOutline } from '~/presentation/icons';
import { useSetupGuideState } from '../useSetupGuideState';
import { useAfterRender } from '~/hooks/useAfterRender';
import { ISetupGuideButtonStatus } from '../types';
import { useWindowSize } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { useToggle } from '~/hooks/useToggle';
import { GTooltip } from '~/presentation/ui';
import { useEffect } from 'react';
import { cls } from '~/utils';

interface IGSetupGuideItemProps {
  item: IGSetupGuideItem;
  expanded: boolean;
  tooltipContent?: string;
  nextItemId: string | undefined;
  buttonStatus?: ISetupGuideButtonStatus;
  setExpandedId: Dispatch<SetStateAction<string | undefined>>;
  handleMarkDone: (highlightedSetupId: string) => void;
}

export const GSetupGuideItem = ({
  item,
  expanded,
  tooltipContent,
  nextItemId,
  buttonStatus = ISetupGuideButtonStatus.NORMAL,
  setExpandedId,
  handleMarkDone,
}: IGSetupGuideItemProps) => {
  const { t } = useTranslation();
  const { isRendered } = useAfterRender();
  const { windowWidth } = useWindowSize();
  const { setHideSetupGuide } = useSetupGuideState();
  const { highlightedSetupId, handleOpenHighLight, handleCloseHighLight } = useGSetupGuideContext();

  const {
    value: isShowHighlight,
    toggle: toggleHighlight,
    setFalse: setFalseHighlight,
  } = useToggle(false);

  const handleExpand = () => {
    if (expanded) return;
    handleCloseHighLight();
    setExpandedId(item.id);
  };

  const handleClick = () => {
    toggleHighlight();
    handleMarkDone(item.id);

    windowWidth['1040Down'] && setHideSetupGuide();
    if (!isShowHighlight) return handleOpenHighLight(item.id);

    if (!nextItemId) return;
    handleCloseHighLight();
    setExpandedId(nextItemId);
  };

  const renderButton = () => {
    if (buttonStatus === ISetupGuideButtonStatus.HIDDEN) return null;

    if (buttonStatus === ISetupGuideButtonStatus.DISABLED) {
      return (
        <span>
          <GTooltip content={tooltipContent}>
            <Button disabled>{t('Show me')}</Button>
          </GTooltip>
        </span>
      );
    }

    return (
      <span>
        <Button onClick={handleClick}>
          {t(isShowHighlight && !!nextItemId ? 'Next' : 'Show me')}
        </Button>
      </span>
    );
  };

  useEffect(() => {
    if (!expanded || highlightedSetupId === null) setFalseHighlight();
  }, [expanded, highlightedSetupId, setFalseHighlight]);

  return (
    <div
      className={cls(
        'px-[10px] rounded-lg hover:bg-surface-secondary hover:shadow-[0_1px_3px_0_#3F3F4426]',
        {
          'cursor-pointer py-[6px]': !expanded,
          'py-3 bg-surface-secondary shadow-[0_1px_3px_0_#3F3F4426] my-1': expanded,
        },
      )}
      onClick={handleExpand}
    >
      <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
        <span>
          <Icon source={item.completed ? CheckCircleIcon : CircleOutline} />
        </span>

        <BlockStack gap="200" id={item.id.toString()}>
          <Text as="h4" variant="bodyMd" fontWeight={expanded ? 'semibold' : 'regular'}>
            {item.title}
          </Text>

          <Collapsible
            open={expanded}
            id={item.id.toString()}
            // Forcing a re-render on the initial mount by utilizing a key to resolve the height calculation issue
            key={isRendered ? item.id : `${item.id}-${expanded}`}
          >
            <BlockStack gap="200">
              <GSetupGuideItemDescription description={item.description} />

              {renderButton()}
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </InlineStack>
    </div>
  );
};
